// D51: repays that paid back a borrow from BEFORE MoneyFlow's history ("előzmény"). The mark dialogs and the
// collapsed "Előzményként jelölt visszafizetések" section of /loans. The mark covers only the UNASSIGNED principal of
// the repay; it is reversible and logged by the server.
import { useId, useState } from 'react';
import { deletePrehistory, putPrehistory } from '../api/endpoints';
import type { EventRow, PrehistoryGroupView, PrehistoryRepayView, UnassignedRepayView } from '../api/model';
import { formatDecimal } from '../lib/decimal';
import { formatUtc } from '../lib/time';
import { ErrorNotice } from './Async';
import { CardHead } from './Card';
import { Dialog } from './Dialog';
import { Table, type Column } from './Table';

export const PREHISTORY_TITLE = 'Ez a visszafizetés a rendszer indulása előtti, nem rögzített hitelt fizetett vissza';
export const NOTE_PLACEHOLDER = 'pl. 2019-es Binance hitel, nincs rögzítve';
const OPEN_KEY = 'moneyflow.loans.prehistory.open.v1';

/** The collapsed state is a per-viewer convenience: the page works without storage. */
export function loadPrehistoryOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function storePrehistoryOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    /* storage unavailable: nothing to do */
  }
}

interface RepayRef {
  location: string;
  asset: string;
  repay: Pick<UnassignedRepayView, 'repay_event_id' | 'occurred_at' | 'quantity' | 'interest_quantity'>;
}

function RepayLine({ r }: { r: RepayRef }) {
  return (
    <div className="muted small">
      {r.location} · {formatUtc(r.repay.occurred_at)} UTC · {formatDecimal(r.repay.quantity)} {r.asset} (ebből kamat: {formatDecimal(r.repay.interest_quantity)} {r.asset})
    </div>
  );
}

/** Shared shell: runs `action`, shows a server error inside the dialog (Hungarian, via the reasons map), calls `onDone` on success. */
function useAction(action: () => Promise<EventRow>, onDone: (ev: EventRow) => void, onFailed: () => void) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      onDone(await action());
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
      setBusy(false);
      onFailed();
    }
  };
  return { busy, err, run };
}

export function PrehistoryMarkDialog({ target, unassigned, onClose, onDone, onFailed }: {
  target: RepayRef; unassigned: string; onClose: () => void; onDone: (ev: EventRow) => void; onFailed: () => void;
}) {
  const [note, setNote] = useState('');
  const { busy, err, run } = useAction(() => putPrehistory(target.repay.repay_event_id, note.trim()), onDone, onFailed);
  const id = useId();
  return (
    <Dialog title="Visszafizetés jelölése előzményként" onClose={onClose}>
      <div className="stack" style={{ gap: 10 }} data-testid="prehistory-dialog">
        <RepayLine r={target} />
        <p style={{ margin: 0 }}>
          Ez a visszafizetés a MoneyFlow indulása előtti, nem rögzített hitelt fizetett vissza. Csak a hozzárendelésre váró rész
          (<b className="mono" data-testid="prehistory-dialog-quantity">{formatDecimal(unassigned)} {target.asset}</b>) lesz előzmény:
          ez a tőke ezután nem csökkent egyetlen rögzített hitelt sem, és nem torzítja a kint lévő tőkét, a számolt kamat alapját és a lezárás-ellenőrzést.
        </p>
        <p className="muted small" style={{ margin: 0 }}>A jelölés bármikor visszavonható, és a szerver naplózza (audit). A már kötött rész érintetlen marad.</p>
        <div className="field">
          <label htmlFor={id}>Megjegyzés (nem kötelező)</label>
          <textarea id={id} className="input" maxLength={5000} placeholder={NOTE_PLACEHOLDER} value={note} data-testid="prehistory-note-input" onChange={(e) => setNote(e.target.value)} />
        </div>
        {err ? <ErrorNotice error={err} /> : null}
        <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>Mégse</button>
          <button type="button" className="btn primary" disabled={busy} data-testid="prehistory-confirm" onClick={() => void run()}>Jelölés előzményként</button>
        </div>
      </div>
    </Dialog>
  );
}

export function PrehistoryNoteDialog({ target, note: initial, onClose, onDone, onFailed }: {
  target: RepayRef; note: string; onClose: () => void; onDone: (ev: EventRow) => void; onFailed: () => void;
}) {
  const [note, setNote] = useState(initial);
  const { busy, err, run } = useAction(() => putPrehistory(target.repay.repay_event_id, note.trim()), onDone, onFailed);
  const id = useId();
  return (
    <Dialog title="Előzmény megjegyzése" onClose={onClose}>
      <div className="stack" style={{ gap: 10 }} data-testid="prehistory-note-dialog">
        <RepayLine r={target} />
        <div className="field">
          <label htmlFor={id}>Megjegyzés</label>
          <textarea id={id} className="input" maxLength={5000} placeholder={NOTE_PLACEHOLDER} value={note} data-testid="prehistory-note-edit" onChange={(e) => setNote(e.target.value)} />
        </div>
        <p className="muted small" style={{ margin: 0 }}>A mentés felülírja a korábbi megjegyzést; a változás naplózódik.</p>
        {err ? <ErrorNotice error={err} /> : null}
        <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>Mégse</button>
          <button type="button" className="btn primary" disabled={busy} data-testid="prehistory-note-save" onClick={() => void run()}>Mentés</button>
        </div>
      </div>
    </Dialog>
  );
}

export function PrehistoryUnmarkDialog({ target, onClose, onDone, onFailed }: {
  target: RepayRef; onClose: () => void; onDone: (ev: EventRow) => void; onFailed: () => void;
}) {
  const { busy, err, run } = useAction(() => deletePrehistory(target.repay.repay_event_id), onDone, onFailed);
  return (
    <Dialog title="Előzmény-jelölés visszavonása" onClose={onClose}>
      <div className="stack" style={{ gap: 10 }} data-testid="prehistory-unmark-dialog">
        <RepayLine r={target} />
        <p style={{ margin: 0 }}>A visszafizetés újra a „Hozzárendelésre vár” listába kerül, és a tőke-része újra csökkenti a hitelek kint lévő részét, ha hozzárendeled. Biztosan visszavonod a jelölést?</p>
        {err ? <ErrorNotice error={err} /> : null}
        <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>Mégse</button>
          <button type="button" className="btn primary" disabled={busy} data-testid="prehistory-unmark-yes" onClick={() => void run()}>Igen, visszavonom</button>
        </div>
      </div>
    </Dialog>
  );
}

function prehistoryColumns(g: PrehistoryGroupView, onNote: (r: PrehistoryRepayView) => void, onUnmark: (r: PrehistoryRepayView) => void): Column<PrehistoryRepayView>[] {
  return [
    { key: 'at', label: 'Visszafizetve (UTC)', sortValue: (r) => r.occurred_at, render: (r) => <span className="mono">{formatUtc(r.occurred_at)}</span> },
    { key: 'qty', label: 'Mennyiség', numeric: true, sortValue: (r) => r.quantity, render: (r) => <span className="mono">{formatDecimal(r.quantity)} {g.asset}</span> },
    { key: 'int', label: 'Ebből kamat', numeric: true, sortValue: (r) => r.interest_quantity, render: (r) => <span className="mono">{formatDecimal(r.interest_quantity)} {g.asset}</span> },
    {
      key: 'pre', label: 'Előzmény-rész', numeric: true, help: 'prehistoryPart', ariaLabel: 'előzmény-rész', sortValue: (r) => r.prehistory_quantity,
      render: (r) => <b className="mono" data-testid="prehistory-quantity">{formatDecimal(r.prehistory_quantity)} {g.asset}</b>,
    },
    { key: 'note', label: 'Megjegyzés', wrap: true, sortValue: (r) => r.note, render: (r) => (r.note ? r.note : <span className="muted">—</span>) },
    { key: 'marked', label: 'Megjelölve (UTC)', sortValue: (r) => r.marked_at, render: (r) => <span className="mono">{formatUtc(r.marked_at)}</span> },
    {
      key: 'act', label: 'Művelet', srOnly: true,
      render: (r) => (
        <span className="row-wrap" style={{ flexWrap: 'nowrap' }}>
          <button type="button" className="btn small" data-testid={`prehistory-edit-${r.repay_event_id}`} aria-label={`Megjegyzés szerkesztése: ${formatUtc(r.occurred_at)} UTC ${formatDecimal(r.quantity)} ${g.asset}`} onClick={() => onNote(r)}>Megjegyzés</button>
          <button type="button" className="btn small" data-testid={`prehistory-unmark-${r.repay_event_id}`} aria-label={`Jelölés visszavonása: ${formatUtc(r.occurred_at)} UTC ${formatDecimal(r.quantity)} ${g.asset}`} onClick={() => onUnmark(r)}>Jelölés visszavonása</button>
        </span>
      ),
    },
  ];
}

/** Collapsed by default; grouped like the "Hozzárendelésre vár" list. */
export function PrehistorySection({ groups, open, onToggle, onNote, onUnmark }: {
  groups: PrehistoryGroupView[]; open: boolean; onToggle: () => void;
  onNote: (g: PrehistoryGroupView, r: PrehistoryRepayView) => void; onUnmark: (g: PrehistoryGroupView, r: PrehistoryRepayView) => void;
}) {
  const bodyId = useId();
  const count = groups.reduce((n, g) => n + g.repays.length, 0);
  return (
    <section className="card tight" aria-label="Előzményként jelölt visszafizetések" data-testid="prehistory-section">
      <CardHead
        title={`Előzményként jelölt visszafizetések (${count} db)`}
        hint="A rendszer indulása előtti, nem rögzített hitelek törlesztései: nem várnak hozzárendelésre, és nem csökkentik a rögzített hitelek kint lévő részét."
      >
        <button type="button" className="btn small" aria-expanded={open} aria-controls={bodyId} data-testid="prehistory-toggle" onClick={onToggle}>
          {open ? 'Összecsukás' : 'Megnyitás'}
        </button>
      </CardHead>
      {open ? (
        <div id={bodyId} className="stack" style={{ gap: 12 }} data-testid="prehistory-body">
          {groups.map((g) => (
            <div key={`${g.location_id}-${g.asset}`} className="stack" style={{ gap: 8 }} data-testid={`prehistory-group-${g.asset}`}>
              <div>
                <b>{g.location} · {g.asset}</b>: <span className="mono" data-testid="prehistory-total">{formatDecimal(g.prehistory_quantity)} {g.asset}</span> előzményként jelölve
              </div>
              <Table
                columns={prehistoryColumns(g, (r) => onNote(g, r), (r) => onUnmark(g, r))}
                rows={g.repays}
                rowKey={(r) => r.repay_event_id}
                dense
                defaultSort={{ key: 'at', dir: 'desc' }}
                label={`Előzményként jelölt visszafizetések: ${g.location} ${g.asset}`}
              />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
